import React, { useState, useEffect, useRef } from 'react';
import { documentsAPI, casesAPI, documentVersionsAPI } from '../lib/api';
import { formatDate, formatFileSize, DOCUMENT_TYPES } from '../lib/utils';
import { 
  FileText, 
  Plus, 
  Search,
  Upload,
  File,
  FolderOpen,
  MoreVertical,
  Download,
  Eye,
  Trash2,
  Loader2,
  X,
  FileIcon,
  Image,
  FileSpreadsheet,
  FileType,
  History,
  RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ScrollArea } from '../components/ui/scroll-area';

const getFileIcon = (mimeType) => {
  if (!mimeType) return <File className="h-6 w-6" />;
  if (mimeType.startsWith('image/')) return <Image className="h-6 w-6 text-green-600" />;
  if (mimeType.includes('pdf')) return <FileText className="h-6 w-6 text-red-600" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileSpreadsheet className="h-6 w-6 text-emerald-600" />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <FileType className="h-6 w-6 text-blue-600" />;
  return <File className="h-6 w-6 text-slate-600" />;
};

export const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    case_id: '',
    description: '',
  });
  const fileInputRef = useRef(null);
  
  // Version history state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedDocForVersion, setSelectedDocForVersion] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [newVersionFile, setNewVersionFile] = useState(null);
  const versionFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, casesRes] = await Promise.all([
        documentsAPI.getAll(),
        casesAPI.getAll()
      ]);
      setDocuments(docsRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Max 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setSelectedFile(file);
    if (!formData.name) {
      setFormData(prev => ({ ...prev, name: file.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result.split(',')[1]; // Remove data:mime;base64, prefix
          
          const uploadData = {
            name: formData.name || selectedFile.name,
            file_data: base64Data,
            mime_type: selectedFile.type || 'application/octet-stream',
            case_id: formData.case_id || null,
            description: formData.description || null
          };
          
          await documentsAPI.upload(uploadData);
          toast.success('Document uploaded successfully');
          fetchData();
          resetForm();
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(error.response?.data?.detail || 'Failed to upload document');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Failed to upload:', error);
      toast.error('Failed to upload document');
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', case_id: '', description: '' });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsDialogOpen(false);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentsAPI.delete(docId);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = (doc) => {
    if (doc.file_url) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${API_URL}${doc.file_url}`, '_blank');
    } else {
      toast.error('No file available for download');
    }
  };

  const handleView = (doc) => {
    if (doc.file_url) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${API_URL}${doc.file_url}?view=true`, '_blank');
    } else {
      toast.error('No file available for viewing');
    }
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    return caseItem?.title || '';
  };

  // Version History Functions
  const openVersionHistory = async (doc) => {
    setSelectedDocForVersion(doc);
    setVersionDialogOpen(true);
    setVersionLoading(true);
    
    try {
      const res = await documentVersionsAPI.getVersions(doc.document_id);
      setVersionHistory(res.data.versions || []);
    } catch (error) {
      console.error('Failed to load version history:', error);
      toast.error('Failed to load version history');
      setVersionHistory([]);
    } finally {
      setVersionLoading(false);
    }
  };

  const handleVersionFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setNewVersionFile(file);
  };

  const handleUploadNewVersion = async () => {
    if (!newVersionFile || !selectedDocForVersion) return;
    
    setUploadingVersion(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result.split(',')[1];
          
          await documentVersionsAPI.uploadVersion(selectedDocForVersion.document_id, {
            name: newVersionFile.name,
            file_data: base64Data,
            mime_type: newVersionFile.type || 'application/octet-stream',
            description: `New version uploaded`
          });
          
          toast.success('New version uploaded successfully');
          setNewVersionFile(null);
          
          // Refresh version history
          const res = await documentVersionsAPI.getVersions(selectedDocForVersion.document_id);
          setVersionHistory(res.data.versions || []);
          
          // Refresh documents list
          fetchData();
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(error.response?.data?.detail || 'Failed to upload new version');
        } finally {
          setUploadingVersion(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploadingVersion(false);
      };
      reader.readAsDataURL(newVersionFile);
    } catch (error) {
      toast.error('Failed to upload new version');
      setUploadingVersion(false);
    }
  };

  const handleDownloadVersion = (documentId, version) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${API_URL}/api/files/${documentId}/version/${version}`, '_blank');
  };

  const handleRestoreVersion = async (documentId, version) => {
    if (!window.confirm(`Restore version ${version}? This will create a new current version.`)) return;
    
    try {
      await documentVersionsAPI.restoreVersion(documentId, version);
      toast.success(`Version ${version} restored successfully`);
      
      // Refresh version history
      const res = await documentVersionsAPI.getVersions(documentId);
      setVersionHistory(res.data.versions || []);
      
      // Refresh documents list
      fetchData();
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore version');
    }
  };

  const closeVersionDialog = () => {
    setVersionDialogOpen(false);
    setSelectedDocForVersion(null);
    setVersionHistory([]);
    setNewVersionFile(null);
  };

  const getTypeColor = (type) => {
    const colors = {
      contract: 'bg-blue-100 text-blue-800',
      pleading: 'bg-purple-100 text-purple-800',
      correspondence: 'bg-green-100 text-green-800',
      template: 'bg-yellow-100 text-yellow-800',
      evidence: 'bg-red-100 text-red-800',
      court_filing: 'bg-indigo-100 text-indigo-800',
      uploaded: 'bg-slate-100 text-slate-800',
      other: 'bg-slate-100 text-slate-800',
    };
    return colors[type] || colors.other;
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || doc.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return <PageLoader text="Loading documents..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Documents</h1>
          <p className="text-slate-500 mt-1">Manage your legal documents and files</p>
        </div>
        <Button 
          onClick={() => setIsDialogOpen(true)} 
          className="gap-2" 
          data-testid="upload-document-btn"
        >
          <Upload className="h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-documents-input"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="uploaded">Uploaded</SelectItem>
            <SelectItem value="contract">Contracts</SelectItem>
            <SelectItem value="pleading">Pleadings</SelectItem>
            <SelectItem value="correspondence">Correspondence</SelectItem>
            <SelectItem value="evidence">Evidence</SelectItem>
            <SelectItem value="court_filing">Court Filings</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{documents.length}</p>
                <p className="text-xs text-slate-500">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Upload className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {documents.filter(d => d.type === 'uploaded').length}
                </p>
                <p className="text-xs text-slate-500">Uploaded</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents List */}
      {filteredDocuments.length > 0 ? (
        <Card data-testid="documents-list">
          <CardHeader>
            <CardTitle className="text-lg">All Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredDocuments.map((doc) => (
                <div 
                  key={doc.document_id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                      {getFileIcon(doc.mime_type)}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{doc.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getTypeColor(doc.type)}>
                          {doc.type || 'uploaded'}
                        </Badge>
                        {doc.case_id && (
                          <span className="text-xs text-slate-500">
                            Case: {getCaseName(doc.case_id)}
                          </span>
                        )}
                        {doc.file_size && (
                          <span className="text-xs text-slate-400">
                            {formatFileSize(doc.file_size)}
                          </span>
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{doc.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {formatDate(doc.created_at)}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {doc.file_url && (
                          <>
                            <DropdownMenuItem onClick={() => handleView(doc)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(doc)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openVersionHistory(doc)}>
                              <History className="h-4 w-4 mr-2" />
                              Version History
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleDelete(doc.document_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FolderOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No documents found</h3>
            <p className="text-slate-500 mb-4">
              {searchTerm ? 'Try a different search term' : 'Upload your first document to get started'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
                <Upload className="h-4 w-4" />
                Upload Document
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a file to your document library. Max file size: 10MB
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* File Drop Zone */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                ${selectedFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  {getFileIcon(selectedFile.type)}
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{selectedFile.name}</p>
                    <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2"
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">
                    Click to select a file or drag and drop
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    PDF, DOC, XLS, PPT, TXT, CSV, Images
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Document Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter document name"
                data-testid="document-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="case">Link to Case (Optional)</Label>
              <Select
                value={formData.case_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, case_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="case-select">
                  <SelectValue placeholder="Select a case" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No case</SelectItem>
                  {cases.map(caseItem => (
                    <SelectItem key={caseItem.case_id} value={caseItem.case_id}>
                      {caseItem.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add a description..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!selectedFile || uploading}
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionDialogOpen} onOpenChange={(open) => { if (!open) closeVersionDialog(); }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History
            </DialogTitle>
            <DialogDescription>
              {selectedDocForVersion?.name} - View and manage document versions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Upload New Version */}
            <div className="p-4 border rounded-lg bg-slate-50">
              <h4 className="font-medium text-sm mb-3">Upload New Version</h4>
              <div className="flex items-center gap-3">
                <input
                  ref={versionFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleVersionFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => versionFileInputRef.current?.click()}
                  disabled={uploadingVersion}
                >
                  Select File
                </Button>
                {newVersionFile && (
                  <>
                    <span className="text-sm text-slate-600 truncate flex-1">
                      {newVersionFile.name}
                    </span>
                    <Button 
                      size="sm"
                      onClick={handleUploadNewVersion}
                      disabled={uploadingVersion}
                    >
                      {uploadingVersion ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Upload'
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setNewVersionFile(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Version List */}
            <div className="border rounded-lg">
              <div className="p-3 bg-slate-100 border-b">
                <h4 className="font-medium text-sm">
                  All Versions ({versionHistory.length})
                </h4>
              </div>
              <ScrollArea className="h-[300px]">
                {versionLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : versionHistory.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No version history available</p>
                    <p className="text-sm mt-1">Upload a new version to start tracking changes</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {versionHistory.map((version, index) => (
                      <div 
                        key={version.version || index}
                        className="p-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700">
                              v{version.version}
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {version.original_name || version.filename}
                                {index === 0 && (
                                  <Badge className="ml-2 text-xs bg-green-100 text-green-700">Current</Badge>
                                )}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatDate(version.uploaded_at || version.created_at)} 
                                {version.size && ` • ${formatFileSize(version.size)}`}
                              </p>
                              {version.change_notes && (
                                <p className="text-xs text-slate-500 mt-0.5 italic">
                                  {version.change_notes}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadVersion(selectedDocForVersion.document_id, version.version)}
                              title="Download this version"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {index > 0 && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleRestoreVersion(selectedDocForVersion.document_id, version.version)}
                                title="Restore this version"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
          
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={closeVersionDialog}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
